import { useAtomValue } from "jotai";
import { useEffect, useRef } from "react";
import { MessageStateAtom, ServerMessage } from "../store";
import useDebounce from "./useDebounce";

/**
 * Interface defining the functionality provided by the useMessageFiltering hook
 * Currently empty as the hook doesn't expose any methods, only handles internal logic
 */
interface UseMessageFiltering {}

/**
 * Hook that provides filtering functionality for incoming server messages
 * Prevents duplicate processing by tracking the timestamp of the last processed message
 *
 * @param onMessage - Callback function that will be invoked with new batches of messages
 * @returns An empty object (hook handles functionality internally)
 *
 * @example
 * // Example usage
 * useMessageFiltering((newMessages) => {
 *   console.log(`Received ${newMessages.length} new messages`);
 *   // Process messages
 * });
 */
export const useMessageFiltering = (
  onMessage: (batch: ServerMessage[]) => void
): UseMessageFiltering => {
  const lastMessageTime = useRef<Date | undefined>(undefined);
  const messages = useAtomValue(MessageStateAtom);

  /**
   * Filters messages and only sends new ones to the callback
   * Tracks the timestamp of the most recent message for future filtering
   *
   * @param messages - Array of server messages to filter
   */
  const filterMessages = (messages: ServerMessage[]) => {
    if (messages.length > 0) {
      // Find the most recent message timestamp
      const maxTime = messages
        .map((x) => x.recievedTime)
        .reduce((prev, current) => {
          if (prev > current) {
            return prev;
          }
          return current;
        }, new Date());

      // Filter to only include messages newer than the last processed message
      const batch = lastMessageTime.current
        ? messages.filter((x) => x.recievedTime > lastMessageTime.current!)
        : messages;
      onMessage(batch);
      lastMessageTime.current = maxTime;
    }
  };

  // Create a debounced version of filterMessages to prevent excessive calls
  const filterMessagesDebounced = useDebounce(filterMessages, 250);

  // Process messages whenever the message state changes
  useEffect(() => {
    filterMessagesDebounced(messages);
  }, [messages]);

  return {};
};
